// src/mappers/influencerMapper.js
import { buildMetrics } from "./metricConfig";
import {
  sanitizeCreatorRawSource,
  sanitizeCreatorText,
  sanitizeCreatorUrl,
} from "../utils/creatorPrivacy";

/* -----------------------------
   Safe value helpers
------------------------------ */

const unwrapDynamoValue = (value) => {
  if (value === null || value === undefined) return value;

  if (typeof value !== "object" || Array.isArray(value)) return value;

  if (Object.prototype.hasOwnProperty.call(value, "S")) return value.S;
  if (Object.prototype.hasOwnProperty.call(value, "N")) return value.N;
  if (Object.prototype.hasOwnProperty.call(value, "BOOL")) return value.BOOL;
  if (Object.prototype.hasOwnProperty.call(value, "NULL")) return null;

  if (Object.prototype.hasOwnProperty.call(value, "L")) {
    return Array.isArray(value.L) ? value.L.map(unwrapDynamoValue) : [];
  }

  if (Object.prototype.hasOwnProperty.call(value, "M")) {
    return unwrapDynamoObject(value.M);
  }

  return value;
};

const unwrapDynamoObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;

  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key] = unwrapDynamoValue(value);
    return acc;
  }, {});
};

const readValue = (obj, ...keys) => {
  if (!obj) return null;

  for (const key of keys) {
    const value = obj?.[key];

    if (value !== undefined && value !== null) {
      return unwrapDynamoValue(value);
    }
  }

  return null;
};

const toNumberOrNull = (v) => {
  const value = unwrapDynamoValue(v);

  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toBooleanOrNull = (v) => {
  const value = unwrapDynamoValue(v);

  if (typeof value === "boolean") return value;
  if (value === null || value === undefined || value === "") return null;

  const s = String(value).trim().toLowerCase();

  if (["true", "1", "yes", "y", "on"].includes(s)) return true;
  if (["false", "0", "no", "n", "off"].includes(s)) return false;

  return null;
};

const toObject = (value) => {
  const unwrapped = unwrapDynamoValue(value);

  if (!unwrapped || typeof unwrapped !== "object" || Array.isArray(unwrapped)) {
    return {};
  }

  return unwrapped;
};

const toArray = (value) => {
  const unwrapped = unwrapDynamoValue(value);

  if (Array.isArray(unwrapped)) {
    return unwrapped.filter((v) => v !== null && v !== undefined && String(v).trim());
  }

  if (typeof unwrapped === "string" && unwrapped.trim()) {
    return unwrapped
      .split(/[;,|]/g)
      .map((v) => v.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeSlug = (value) => {
  const s = String(value || "").trim().toLowerCase();
  return s || null;
};

const normalizeUsername = (u) => {
  const s = String(u || "").trim();
  if (!s) return null;
  return s.replace(/^@/, "");
};

const normalizeUrl = (url) => {
  const raw = unwrapDynamoValue(url);
  if (!raw) return null;

  const s = String(raw).trim();
  if (!s) return null;

  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("//")) return `https:${s}`;
  if (s.startsWith("/")) return s;
  if (/^[a-z0-9.-]+\.[a-z]{2,}\/?/i.test(s)) return `https://${s}`;

  return s;
};

const buildPlatformProfileUrl = ({ platform, username, channelId }) => {
  const normalizedPlatform = normalizeSlug(platform);
  const normalizedUsername = normalizeUsername(username);
  const normalizedChannelId = String(channelId || "").trim();

  if (normalizedPlatform === "instagram" && normalizedUsername) {
    return `https://www.instagram.com/${encodeURIComponent(normalizedUsername)}/`;
  }

  if (normalizedPlatform === "youtube") {
    if (normalizedChannelId) {
      return `https://www.youtube.com/channel/${encodeURIComponent(normalizedChannelId)}`;
    }

    if (normalizedUsername) {
      return `https://www.youtube.com/@${encodeURIComponent(normalizedUsername)}`;
    }
  }

  if (normalizedPlatform === "tiktok" && normalizedUsername) {
    return `https://www.tiktok.com/@${encodeURIComponent(normalizedUsername)}`;
  }

  return null;
};

const normalizeTimestamp = (value) => {
  const raw = unwrapDynamoValue(value);
  if (!raw) return null;

  const s = String(raw).trim();
  if (!s) return null;

  // Safari-safe conversion: 2026-05-01T13:54:24+0000 -> 2026-05-01T13:54:24+00:00
  const fixed = s.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");

  const d = new Date(fixed);
  if (Number.isNaN(d.getTime())) return s;

  return d.toISOString();
};

const uniqueStrings = (values) => {
  const seen = new Set();
  const output = [];

  values.forEach((value) => {
    const s = String(value || "").trim();
    if (!s) return;

    const key = s.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    output.push(s);
  });

  return output;
};

/* -----------------------------
   Media / recent posts
------------------------------ */

const getRawMediaRows = (item) => {
  if (!item) return [];

  const recentPosts = readValue(item, "recent_posts", "recentPosts");
  if (Array.isArray(recentPosts)) return recentPosts;

  const rawSource = readValue(item, "raw_source", "rawSource") || {};

  const rawMedia = readValue(rawSource, "media");
  const rawMediaData = readValue(rawMedia, "data");

  if (Array.isArray(rawMediaData)) return rawMediaData;

  const directMedia = readValue(item, "media");
  if (Array.isArray(directMedia)) return directMedia;

  const directMediaData = readValue(directMedia, "data");
  if (Array.isArray(directMediaData)) return directMediaData;

  return [];
};

const normalizeRecentPost = (post, idx) => {
  const source = unwrapDynamoValue(post) || {};

  const id = readValue(source, "id") || `post-${idx}`;
  const timestamp = normalizeTimestamp(
    readValue(source, "timestamp", "created_time", "createdTime")
  );

  const captionRaw = readValue(source, "caption");
  const caption = captionRaw ? sanitizeCreatorText(captionRaw) : "";

  return {
    id: String(id),
    timestamp,
    media_type: String(readValue(source, "media_type", "mediaType") || "POST"),
    like_count: toNumberOrNull(readValue(source, "like_count", "likeCount")),
    comments_count: toNumberOrNull(
      readValue(source, "comments_count", "commentsCount")
    ),
    view_count: toNumberOrNull(readValue(source, "view_count", "viewCount")),
    caption,
    media_url: normalizeUrl(readValue(source, "media_url", "mediaUrl")),
    thumbnail_url: normalizeUrl(
      readValue(source, "thumbnail_url", "thumbnailUrl")
    ),
    permalink: normalizeUrl(readValue(source, "permalink", "url")),
  };
};

const normalizeRecentPosts = (item) => {
  return getRawMediaRows(item)
    .map(normalizeRecentPost)
    .filter((post) => post.timestamp || post.caption || post.media_url);
};

/* -----------------------------
   Image / identity
------------------------------ */

const findProfileImage = (item) => {
  if (!item) return null;

  const rawSource = readValue(item, "raw_source", "rawSource") || {};
  const profile = readValue(item, "profile") || {};
  const recentPosts = normalizeRecentPosts(item);

  const candidates = [
    // top-level common fields
    readValue(item, "profile_image_url"),
    readValue(item, "profileImageUrl"),
    readValue(item, "profile_pic_url"),
    readValue(item, "profile_picture_url"),
    readValue(item, "profilePictureUrl"),
    readValue(item, "avatar_url"),
    readValue(item, "avatarUrl"),
    readValue(item, "thumbnail_url"),
    readValue(item, "picture_url"),
    readValue(item, "image_url"),
    readValue(item, "profile_picture"),
    readValue(item, "avatar"),
    readValue(item, "image"),

    // raw source variations
    readValue(rawSource, "profile_image_url"),
    readValue(rawSource, "profileImageUrl"),
    readValue(rawSource, "profile_pic_url"),
    readValue(rawSource, "profile_picture_url"),
    readValue(rawSource, "profilePictureUrl"),
    readValue(rawSource, "profile_picture"),
    readValue(rawSource, "avatar_url"),
    readValue(rawSource, "thumbnail_url"),
    readValue(rawSource, "picture"),
    readValue(rawSource, "image_url"),

    // nested profile object
    readValue(profile, "picture"),
    readValue(profile, "profile_pic_url"),
    readValue(profile, "profile_picture_url"),
    readValue(profile, "avatar_url"),
    readValue(profile, "image_url"),

    // last-resort content fallback
    recentPosts?.[0]?.thumbnail_url,
    recentPosts?.[0]?.media_url,
  ];

  for (const url of candidates) {
    const normalized = normalizeUrl(url);
    if (normalized) return normalized;
  }

  return null;
};

const resolveCanonicalId = (item, { routeId } = {}) => {
  const platform = normalizeSlug(readValue(item, "platform"));

  const username = normalizeUsername(
    readValue(item, "username", "handle", "screen_name")
  );

  const externalId = readValue(item, "external_id", "externalId");
  const rawSource = readValue(item, "raw_source", "rawSource") || {};

  if (platform === "instagram") {
    return (
      externalId ||
      readValue(rawSource, "id") ||
      (typeof readValue(item, "id") === "string" && /^\d+$/.test(readValue(item, "id"))
        ? readValue(item, "id")
        : null) ||
      username ||
      routeId ||
      null
    );
  }

  if (platform === "youtube") {
    return (
      readValue(item, "channelId", "channel_id") ||
      readValue(rawSource, "channel_id", "channelId") ||
      externalId ||
      readValue(item, "id") ||
      routeId ||
      null
    );
  }

  return (
    externalId ||
    readValue(item, "channelId", "channel_id") ||
    readValue(item, "id") ||
    username ||
    routeId ||
    null
  );
};

/* -----------------------------
   Public UI mapper
------------------------------ */

export const mapApiInfluencerToUiModel = (item, opts = {}) => {
  if (!item) return null;

  const rawSource = readValue(item, "raw_source", "rawSource") || null;
  const sanitizedRawSource = sanitizeCreatorRawSource(rawSource);

  const platform = normalizeSlug(readValue(item, "platform"));

  const externalId = readValue(item, "external_id", "externalId") ?? null;
  const channelId =
    readValue(item, "channelId", "channel_id") ||
    readValue(rawSource, "channel_id", "channelId") ||
    null;

  // Do not use DynamoDB pk/sk for UI identity.
  const canonicalId = resolveCanonicalId(item, { routeId: opts?.routeId });

  const routeId =
    opts?.routeId ??
    readValue(item, "_routeId", "routeId") ??
    null;

  const username = normalizeUsername(
    readValue(item, "username", "handle", "screen_name")
  );

  const fullName =
    readValue(item, "full_name", "fullName", "name") ||
    readValue(rawSource, "name") ||
    null;

  const profileImage = findProfileImage(item);

  const profileUrl =
    normalizeUrl(
      readValue(
        item,
        "profile_url",
        "profileUrl",
        "youtube_channel_url",
        "youtubeChannelUrl",
        "channel_url",
        "channelUrl",
        "tiktok_profile_url",
        "tiktokProfileUrl",
        "url",
        "link"
      )
    ) ||
    buildPlatformProfileUrl({ platform, username, channelId });

  const website =
    sanitizeCreatorUrl(
      normalizeUrl(readValue(item, "website", "website_url", "websiteUrl"))
    ) ||
    sanitizeCreatorUrl(
      normalizeUrl(readValue(rawSource, "website", "website_url", "websiteUrl"))
    ) ||
    null;

  const nicheSlug = normalizeSlug(readValue(item, "niche_slug", "nicheSlug"));
  const nicheParent = normalizeSlug(
    readValue(item, "niche_parent", "nicheParent")
  );
  const citySlug = normalizeSlug(readValue(item, "city_slug", "citySlug", "city"));

  const tagsArray = uniqueStrings(toArray(readValue(item, "tags")));
  const topicTagsArray = uniqueStrings(toArray(readValue(item, "topic_tags", "topicTags")));
  const hashtagsArray = uniqueStrings(toArray(readValue(item, "hashtags")));
  const aiStyleTags = uniqueStrings(toArray(readValue(item, "ai_style_tags", "aiStyleTags")));
  const aiRiskFlags = uniqueStrings(toArray(readValue(item, "ai_risk_flags", "aiRiskFlags")));

  // Preserve the authoritative backend freshness envelope. Components and
  // selectors must consume this contract instead of deriving trust from
  // browser fetch time or a generic database updated_at timestamp.
  const rawDataQuality = toObject(
    readValue(item, "data_quality", "dataQuality")
  );
  const metricFreshness = toObject(
    readValue(item, "metric_freshness", "metricFreshness")
  );

  // The detail and feed endpoints may expose the same freshness contract at
  // different levels during migration. Resolve one canonical value here so a
  // nested metrics_observed_at is never lost merely because the top-level field
  // is absent or null. The backend remains authoritative; updated_at is never
  // used as metric proof.
  const resolvedMetricsObservedAt = normalizeTimestamp(
    readValue(rawDataQuality, "metrics_observed_at", "metricsObservedAt") ??
      readValue(item, "metrics_observed_at", "metricsObservedAt") ??
      readValue(item, "source_refreshed_at", "sourceRefreshedAt") ??
      readValue(item, "last_refreshed_at", "lastRefreshedAt")
  );

  const resolvedFreshnessStatus =
    readValue(rawDataQuality, "freshness_status", "freshnessStatus") ??
    readValue(item, "freshness_status", "freshnessStatus") ??
    null;

  const dataQuality = {
    ...rawDataQuality,
    freshness_status: resolvedFreshnessStatus,
    metrics_observed_at: resolvedMetricsObservedAt,
  };

  const access = toObject(readValue(item, "access"));
  const refreshPolicy = toObject(
    readValue(item, "refresh_policy", "refreshPolicy")
  );
  const detailsLoaded =
    toBooleanOrNull(readValue(item, "details_loaded", "detailsLoaded")) === true;
  const detailLevel =
    readValue(item, "detail_level", "detailLevel") ||
    readValue(access, "level") ||
    null;

  const displayCategory = nicheSlug || topicTagsArray[0] || tagsArray[0] || "—";
  const displayLocation = citySlug || "—";

  const followers = toNumberOrNull(
    readValue(item, "followers", "follower_count", "followerCount", "subscriberCount") ??
      readValue(rawSource, "followers", "followers_count", "follower_count")
  );

  const following = toNumberOrNull(
    readValue(item, "following", "following_count", "followingCount") ??
      readValue(rawSource, "follows_count", "following", "following_count")
  );

  const mediaCount = toNumberOrNull(
    readValue(item, "media_count", "mediaCount", "videoCount") ??
      readValue(rawSource, "media_count", "mediaCount")
  );

  const avgEng30 = toNumberOrNull(
    readValue(item, "avg_engagement_rate_30d", "avgEngagementRate30d") ??
      readValue(item, "avg_engagement_rate", "engagement_rate", "engagementRate")
  );

  const avgEng90 = toNumberOrNull(
    readValue(item, "avg_engagement_rate_90d", "avgEngagementRate90d")
  );

  const recentPosts = normalizeRecentPosts(item);

  const ui = {
    /* -----------------------------
       Identity
    ------------------------------ */
    keyId: canonicalId || routeId || username || externalId || channelId || null,
    id: canonicalId || routeId || username || externalId || channelId || null,
    canonical_id: canonicalId || null,
    _routeId: routeId || null,

    external_id: externalId,
    externalId,
    channelId,

    username,
    handle: username,
    name: fullName || username || "Unknown Influencer",
    full_name: fullName,

    platform,

    /* -----------------------------
       Public profile
    ------------------------------ */
    bio: sanitizeCreatorText(
      readValue(item, "bio", "biography", "description") ??
        readValue(rawSource, "biography", "bio", "description") ??
        ""
    ) || null,

    website,

    profile_image_url: profileImage,
    profileImageUrl: profileImage,

    profile_url: profileUrl,
    profileUrl,

    /* -----------------------------
       Segment / classification
    ------------------------------ */
    category: displayCategory,
    displayCategory,

    city_slug: citySlug,
    citySlug,
    city: citySlug,
    displayLocation,

    niche_slug: nicheSlug,
    nicheSlug,
    niche_parent: nicheParent,
    nicheParent,

    influencer_tier: readValue(item, "influencer_tier", "tier", "tier_label") ?? null,
    tier: readValue(item, "influencer_tier", "tier", "tier_label") ?? null,

    /* -----------------------------
       Audience / counts
    ------------------------------ */
    followers,
    followerCount: followers,
    subscriberCount: followers,

    following,
    media_count: mediaCount,
    mediaCount,

    /* -----------------------------
       Activity / engagement
    ------------------------------ */
    posts_last_30d: toNumberOrNull(readValue(item, "posts_last_30d", "postsLast30d")),
    postsLast30d: toNumberOrNull(readValue(item, "posts_last_30d", "postsLast30d")),

    posts_last_90d: toNumberOrNull(readValue(item, "posts_last_90d", "postsLast90d")),
    postsLast90d: toNumberOrNull(readValue(item, "posts_last_90d", "postsLast90d")),

    posts_per_week_30d: toNumberOrNull(
      readValue(item, "posts_per_week_30d", "postsPerWeek30d")
    ),
    postsPerWeek30d: toNumberOrNull(
      readValue(item, "posts_per_week_30d", "postsPerWeek30d")
    ),

    avg_engagement_rate: toNumberOrNull(
      readValue(item, "avg_engagement_rate", "engagement_rate", "engagementRate") ??
        avgEng30
    ),
    engagement_rate: toNumberOrNull(
      readValue(item, "avg_engagement_rate", "engagement_rate", "engagementRate") ??
        avgEng30
    ),

    avg_engagement_rate_30d: avgEng30,
    avgEngagementRate30d: avgEng30,

    avg_engagement_rate_90d: avgEng90,
    avgEngagementRate90d: avgEng90,

    avg_likes_per_post_30d: toNumberOrNull(
      readValue(item, "avg_likes_per_post_30d", "avgLikesPerPost30d")
    ),
    avgLikesPerPost30d: toNumberOrNull(
      readValue(item, "avg_likes_per_post_30d", "avgLikesPerPost30d")
    ),

    avg_comments_per_post_30d: toNumberOrNull(
      readValue(item, "avg_comments_per_post_30d", "avgCommentsPerPost30d")
    ),
    avgCommentsPerPost30d: toNumberOrNull(
      readValue(item, "avg_comments_per_post_30d", "avgCommentsPerPost30d")
    ),

    avg_views_per_post_30d: toNumberOrNull(
      readValue(item, "avg_views_per_post_30d", "avgViewsPerPost30d")
    ),
    avgViewsPerPost30d: toNumberOrNull(
      readValue(item, "avg_views_per_post_30d", "avgViewsPerPost30d")
    ),

    engagement_rate_basis:
      readValue(item, "engagement_rate_basis", "engagementRateBasis") ?? null,
    engagementRateBasis:
      readValue(item, "engagement_rate_basis", "engagementRateBasis") ?? null,

    engagement_sample_posts_30d: toNumberOrNull(
      readValue(item, "engagement_sample_posts_30d", "engagementSamplePosts30d")
    ),
    engagementSamplePosts30d: toNumberOrNull(
      readValue(item, "engagement_sample_posts_30d", "engagementSamplePosts30d")
    ),

    engagement_sample_posts_90d: toNumberOrNull(
      readValue(item, "engagement_sample_posts_90d", "engagementSamplePosts90d")
    ),
    engagementSamplePosts90d: toNumberOrNull(
      readValue(item, "engagement_sample_posts_90d", "engagementSamplePosts90d")
    ),

    engagement_label_30d:
      readValue(item, "engagement_label_30d", "engagementLabel30d") ?? null,
    engagementLabel30d:
      readValue(item, "engagement_label_30d", "engagementLabel30d") ?? null,

    engagement_band_30d:
      readValue(item, "engagement_band_30d", "engagementBand30d") ?? null,
    engagementBand30d:
      readValue(item, "engagement_band_30d", "engagementBand30d") ?? null,

    post_frequency_label_30d:
      readValue(item, "post_frequency_label_30d", "postFrequencyLabel30d") ?? null,
    postFrequencyLabel30d:
      readValue(item, "post_frequency_label_30d", "postFrequencyLabel30d") ?? null,

    post_frequency_band_30d:
      readValue(item, "post_frequency_band_30d", "postFrequencyBand30d") ?? null,
    postFrequencyBand30d:
      readValue(item, "post_frequency_band_30d", "postFrequencyBand30d") ?? null,

    last_post_at: normalizeTimestamp(
      readValue(item, "last_post_at", "lastPostAt")
    ),
    lastPostAt: normalizeTimestamp(
      readValue(item, "last_post_at", "lastPostAt")
    ),

    last_post_epoch: toNumberOrNull(readValue(item, "last_post_epoch", "lastPostEpoch")),
    lastPostEpoch: toNumberOrNull(readValue(item, "last_post_epoch", "lastPostEpoch")),

    is_active: toBooleanOrNull(readValue(item, "is_active", "isActive")),
    isActive: toBooleanOrNull(readValue(item, "is_active", "isActive")),

    /* -----------------------------
       Commercial / AI scores
    ------------------------------ */
    activity_score: toNumberOrNull(readValue(item, "activity_score", "activityScore")),
    activityScore: toNumberOrNull(readValue(item, "activity_score", "activityScore")),

    locality_score: toNumberOrNull(readValue(item, "locality_score", "localityScore")),
    localityScore: toNumberOrNull(readValue(item, "locality_score", "localityScore")),

    quality_score: toNumberOrNull(readValue(item, "quality_score", "qualityScore")),
    qualityScore: toNumberOrNull(readValue(item, "quality_score", "qualityScore")),

    trend_score: toNumberOrNull(readValue(item, "trend_score", "trendScore")),
    trendScore: toNumberOrNull(readValue(item, "trend_score", "trendScore")),

    collaboration_score: toNumberOrNull(
      readValue(item, "collaboration_score", "collaborationScore")
    ),
    collaborationScore: toNumberOrNull(
      readValue(item, "collaboration_score", "collaborationScore")
    ),

    account_fit_score: toNumberOrNull(
      readValue(item, "account_fit_score", "accountFitScore")
    ),
    accountFitScore: toNumberOrNull(
      readValue(item, "account_fit_score", "accountFitScore")
    ),

    relevance_score: toNumberOrNull(readValue(item, "relevance_score", "relevanceScore")),
    relevanceScore: toNumberOrNull(readValue(item, "relevance_score", "relevanceScore")),

    ai_version: readValue(item, "ai_version", "aiVersion") ?? null,
    aiVersion: readValue(item, "ai_version", "aiVersion") ?? null,

    ai_relevance_reason:
      sanitizeCreatorText(
        readValue(item, "ai_relevance_reason", "aiRelevanceReason")
      ) || null,
    aiRelevanceReason:
      sanitizeCreatorText(
        readValue(item, "ai_relevance_reason", "aiRelevanceReason")
      ) || null,

    ai_style_tags: aiStyleTags,
    aiStyleTags,

    ai_risk_flags: aiRiskFlags,
    aiRiskFlags,

    risks: aiRiskFlags,
    risk_flags: aiRiskFlags,
    riskFlags: aiRiskFlags,

    /* -----------------------------
       Geo / validation
    ------------------------------ */
    is_geo_match: toBooleanOrNull(readValue(item, "is_geo_match", "isGeoMatch")),
    isGeoMatch: toBooleanOrNull(readValue(item, "is_geo_match", "isGeoMatch")),

    geo_confidence: readValue(item, "geo_confidence", "geoConfidence") ?? null,
    geoConfidence: readValue(item, "geo_confidence", "geoConfidence") ?? null,

    geo_notes: readValue(item, "geo_notes", "geoNotes") ?? null,
    geoNotes: readValue(item, "geo_notes", "geoNotes") ?? null,

    validation_status:
      readValue(item, "validation_status", "validationStatus") ?? null,
    validationStatus:
      readValue(item, "validation_status", "validationStatus") ?? null,

    validation_flags: uniqueStrings(
      toArray(readValue(item, "validation_flags", "validationFlags"))
    ),
    validationFlags: uniqueStrings(
      toArray(readValue(item, "validation_flags", "validationFlags"))
    ),

    /* -----------------------------
       Taxonomies
    ------------------------------ */
    tags: tagsArray,
    topic_tags: topicTagsArray,
    topicTags: topicTagsArray,
    hashtags: hashtagsArray,

    /* -----------------------------
       Freshness
    ------------------------------ */
    data_quality: dataQuality,
    dataQuality,
    metric_freshness: metricFreshness,
    metricFreshness,

    metrics_observed_at: resolvedMetricsObservedAt,
    metricsObservedAt: resolvedMetricsObservedAt,

    source_refreshed_at: normalizeTimestamp(
      readValue(item, "source_refreshed_at", "sourceRefreshedAt")
    ),
    sourceRefreshedAt: normalizeTimestamp(
      readValue(item, "source_refreshed_at", "sourceRefreshedAt")
    ),

    last_refreshed_at: normalizeTimestamp(
      readValue(item, "last_refreshed_at", "lastRefreshedAt")
    ),
    lastRefreshedAt: normalizeTimestamp(
      readValue(item, "last_refreshed_at", "lastRefreshedAt")
    ),

    freshness_status: resolvedFreshnessStatus,
    freshnessStatus: resolvedFreshnessStatus,

    created_at: normalizeTimestamp(readValue(item, "created_at", "createdAt")),
    createdAt: normalizeTimestamp(readValue(item, "created_at", "createdAt")),

    updated_at: normalizeTimestamp(readValue(item, "updated_at", "updatedAt")),
    updatedAt: normalizeTimestamp(readValue(item, "updated_at", "updatedAt")),

    /* -----------------------------
       Public evidence for details page
    ------------------------------ */
    recent_posts: recentPosts,
    recentPosts,

    // Defence in depth only: the API should omit restricted contact fields.
    // The compatibility raw source retained in Redux is sanitized before use.
    raw_source: sanitizedRawSource,
    rawSource: sanitizedRawSource,

    details_loaded: detailsLoaded,
    detailsLoaded,
    detail_level: detailLevel,
    detailLevel,
    access,
    refresh_policy: refreshPolicy,
    refreshPolicy,
  };

  ui.metrics = buildMetrics(ui);

  return ui;
};